<template>
  <div class="pickup-screen">
    <!-- 顶部装饰条 -->
    <div class="top-decoration"></div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 左侧：准备中 -->
      <div class="preparing-section">
        <!-- 背景光效 -->
        <div class="bg-glow preparing-glow"></div>
        <div class="bg-pattern"></div>

        <div class="section-header">
          <div class="title-wrapper">
            <div class="title-icon">
              <i class="fas fa-clock"></i>
            </div>
            <h2 class="section-title">
              <span class="title-main">备餐中</span>
              <span class="title-sub">Preparing</span>
            </h2>
          </div>
          <div class="section-count">{{ preparingOrders.length }}</div>
        </div>

        <div class="order-numbers-container">
          <transition-group
            name="order-list"
            tag="div"
            class="order-list-wrapper preparing-list"
            @before-leave="freezeLeavingCard"
            @after-leave="clearLeavingCard"
            @before-enter="clearLeavingCard"
          >
            <div
              v-for="item in preparingOrders"
              :key="`preparing-${item.transitionKey}`"
              class="order-number-item"
              :class="{ 'vip-order': isVipOrder(item) }"
            >
              <div class="order-item-glow"></div>
              <div class="order-content">
                <i v-if="isVipOrder(item)" class="fas fa-crown vip-icon"></i>
                <span class="order-number">{{ getPickupNumber(item) }}</span>
              </div>
            </div>
          </transition-group>
          <div v-if="preparingOrders.length === 0" class="empty-state">
            <i class="fas fa-inbox empty-icon"></i>
            <p class="empty-text">暂无准备中的订单</p>
          </div>
        </div>
      </div>

      <!-- 右侧：请取餐 -->
      <div class="ready-section">
        <!-- 背景光效 -->
        <div class="bg-glow ready-glow"></div>
        <div class="bg-pattern ready-pattern"></div>

        <div class="section-header">
          <div class="title-wrapper">
            <div class="title-icon ready-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h2 class="section-title">
              <span class="title-main">请取餐</span>
              <span class="title-sub">Ready</span>
            </h2>
          </div>
          <div class="section-count ready-count">{{ readyOrders.length }}</div>
        </div>

        <div class="order-numbers-container ready-orders">
          <transition-group
            name="order-list"
            tag="div"
            class="order-list-wrapper ready-list"
            @before-leave="freezeLeavingCard"
            @after-leave="clearLeavingCard"
            @before-enter="clearLeavingCard"
          >
            <div
              v-for="item in readyOrders"
              :key="`ready-${item.transitionKey}`"
              class="order-number-item ready-item"
              :class="{ 'vip-order': isVipOrder(item) }"
            >
              <div class="order-item-glow ready-glow-item"></div>
              <div class="order-content">
                <i v-if="isVipOrder(item)" class="fas fa-crown vip-icon"></i>
                <span class="order-number">{{ getPickupNumber(item) }}</span>
              </div>
            </div>
          </transition-group>
          <div v-if="readyOrders.length === 0" class="empty-state">
            <i class="fas fa-inbox empty-icon"></i>
            <p class="empty-text">暂无待取餐的订单</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部装饰条 -->
    <div class="bottom-decoration"></div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, onMounted, onUnmounted } from 'vue';
  import { getOrderPick } from '@/service/order';
  import { fetchShopDetail, fetchShopList } from '@/service/base';
  import { readSelectedShop } from '@/utils/selectedShop';
  import { OrderPickModel, OrderPickItemModel } from '@/model/orderPick';
  import { ShopDetailModel } from '@/model/shop';

  // 响应式数据
  const shopId = ref<number | null>(null);
  const shopInfo = ref<ShopDetailModel | null>(null);
  const pickData = ref<OrderPickModel | null>(null);
  const lastRefreshTime = ref<string>('');

  // 店铺选择相关
  const shopList = ref<any[]>([]);

  // 自动刷新定时器
  let autoRefreshTimer: NodeJS.Timeout | null = null;

  // 同一取餐码可能重复，使用出现次数区分，避免列表补位触发无关号码重新入场。
  const getKeyedOrders = (orders: OrderPickItemModel[]) => {
    const occurrences = new Map<string, number>();
    return orders.map((item) => {
      const occurrence = occurrences.get(item.pickCode) ?? 0;
      occurrences.set(item.pickCode, occurrence + 1);
      return { ...item, transitionKey: `${item.pickCode}-${occurrence}` };
    });
  };

  // 离场卡片脱离网格，保留原位置，让其余卡片平滑补位。
  const freezeLeavingCard = (element: Element) => {
    if (!(element instanceof HTMLElement)) return;
    const { offsetLeft, offsetTop, offsetWidth, offsetHeight } = element;
    element.style.left = `${offsetLeft}px`;
    element.style.top = `${offsetTop}px`;
    element.style.width = `${offsetWidth}px`;
    element.style.height = `${offsetHeight}px`;
  };

  const clearLeavingCard = (element: Element) => {
    if (!(element instanceof HTMLElement)) return;
    ['left', 'top', 'width', 'height'].forEach((property) => {
      element.style.removeProperty(property);
    });
  };

  // 计算属性：准备中的订单（waitOutList：待出餐）
  const preparingOrders = computed(() => {
    return getKeyedOrders(pickData.value?.waitOutList || []);
  });

  // 计算属性：请取餐的订单（completeOutList：已出餐）
  const readyOrders = computed(() => {
    return getKeyedOrders(pickData.value?.completeOutList || []);
  });

  // 获取取餐号码（直接使用后端返回的取餐码）
  const getPickupNumber = (item: OrderPickItemModel): string => {
    if (item.pickCode) {
      return item.pickCode;
    }
    return '0000';
  };

  // 判断是否是VIP订单
  const isVipOrder = (item: OrderPickItemModel): boolean => {
    // 可以根据实际业务逻辑判断，例如：
    // 1. 取餐号包含特殊前缀（如V、VIP等）
    // 2. 取餐号有VIP标识
    // 示例：return item.pickCode?.includes('V') || item.pickCode?.startsWith('V') || false;
    return false;
  };

  // 获取餐厅信息
  const fetchShopInfo = async (id: number) => {
    try {
      const response = await fetchShopDetail({ shopId: id });
      if (response.code === 200 && response.result) {
        shopInfo.value = response.result;
      }
    } catch (error) {
      console.error('获取餐厅信息失败:', error);
    }
  };

  // 加载当前账号可见的餐厅，用于解析缓存餐厅名称。
  const loadShopList = async () => {
    try {
      const response = await fetchShopList({
        pageNum: 1,
        pageSize: 999,
        data: { shopStatus: 0 },
      });
      if (response.code === 200 && response.result && response.result.list) {
        shopList.value = response.result.list;
      }
    } catch (error) {
      console.error('加载店铺列表失败:', error);
    }
  };

  // 获取取餐号列表
  const fetchOrders = async (id: number) => {
    try {
      const response = await getOrderPick({
        shopId: id,
      });

      if (response.code === 200) {
        pickData.value = response.result || { waitOutList: [], completeOutList: [] };
        updateRefreshTime();
      } else {
        console.error('获取取餐号列表失败:', response.msg);
        pickData.value = { waitOutList: [], completeOutList: [] };
      }
    } catch (error) {
      console.error('获取取餐号列表失败:', error);
      pickData.value = { waitOutList: [], completeOutList: [] };
    }
  };

  // 更新刷新时间
  const updateRefreshTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    lastRefreshTime.value = `${hours}:${minutes}:${seconds}`;
  };

  // 启动自动刷新
  const startAutoRefresh = () => {
    // 每5秒刷新一次
    autoRefreshTimer = setInterval(() => {
      if (shopId.value) {
        fetchOrders(shopId.value);
      }
    }, 5000);
  };

  // 停止自动刷新
  const stopAutoRefresh = () => {
    if (autoRefreshTimer) {
      clearInterval(autoRefreshTimer);
      autoRefreshTimer = null;
    }
  };

  // 初始化
  onMounted(async () => {
    await loadShopList();
    const id = readSelectedShop();
    if (id) {
      shopId.value = id;
      await Promise.all([fetchShopInfo(id), fetchOrders(id)]);
      startAutoRefresh();
    }
  });

  // 清理
  onUnmounted(() => {
    stopAutoRefresh();
  });
</script>

<style scoped>
  @keyframes float {
    0%,
    100% {
      transform: translateY(0);
    }
    50% {
      transform: translateY(calc(-10 * var(--pickup-unit)));
    }
  }

  @keyframes glow {
    0%,
    100% {
      opacity: 0.5;
    }
    50% {
      opacity: 1;
    }
  }

  @keyframes shimmer {
    0% {
      background-position: calc(-1000 * var(--pickup-unit)) 0;
    }
    100% {
      background-position: calc(1000 * var(--pickup-unit)) 0;
    }
  }

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
    }
    50% {
      transform: scale(1.05);
    }
  }

  .pickup-screen {
    /* 以 1920px 为基准，大屏中所有尺寸共用同一缩放单位。 */
    --pickup-unit: max(1px, calc(100vw / 1920));

    width: 100vw;
    height: 100vh;
    background: linear-gradient(135deg, #0a0a0a 0%, #1a1a1a 50%, #0a0a0a 100%);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    font-family: 'PingFang SC', 'Microsoft YaHei', 'Arial', sans-serif;
    position: relative;
  }

  /* 店铺选择模态框样式 */
  .shop-select-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 50;
  }

  .shop-select-modal {
    background: #ffffff;
    border-radius: calc(16 * var(--pickup-unit));
    padding: calc(24 * var(--pickup-unit)) calc(28 * var(--pickup-unit));
    min-width: calc(360 * var(--pickup-unit));
    box-shadow: 0 calc(20 * var(--pickup-unit)) calc(40 * var(--pickup-unit)) rgba(0, 0, 0, 0.35);
    text-align: center;
  }

  .shop-select-title {
    font-size: calc(20 * var(--pickup-unit));
    font-weight: 600;
    margin-bottom: calc(16 * var(--pickup-unit));
    color: #111827;
  }

  .shop-select-body {
    margin-bottom: calc(20 * var(--pickup-unit));
  }

  .shop-select-dropdown {
    width: 100%;
    padding: calc(10 * var(--pickup-unit)) calc(12 * var(--pickup-unit));
    border-radius: calc(8 * var(--pickup-unit));
    border: calc(1 * var(--pickup-unit)) solid #d1d5db;
    font-size: calc(16 * var(--pickup-unit));
    outline: none;
  }

  .shop-select-dropdown:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 calc(2 * var(--pickup-unit)) rgba(59, 130, 246, 0.2);
  }

  .shop-select-footer {
    display: flex;
    justify-content: center;
  }

  .shop-select-btn.confirm {
    padding: calc(8 * var(--pickup-unit)) calc(32 * var(--pickup-unit));
    border-radius: calc(999 * var(--pickup-unit));
    border: none;
    background: linear-gradient(135deg, #3b82f6, #2563eb);
    color: #ffffff;
    font-size: calc(16 * var(--pickup-unit));
    font-weight: 500;
    cursor: pointer;
    box-shadow: 0 calc(8 * var(--pickup-unit)) calc(16 * var(--pickup-unit)) rgba(37, 99, 235, 0.3);
  }

  .shop-select-btn.confirm:hover {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
  }

  .top-decoration,
  .bottom-decoration {
    height: calc(4 * var(--pickup-unit));
    background: linear-gradient(
      90deg,
      transparent 0%,
      rgba(255, 215, 0, 0.3) 25%,
      rgba(255, 140, 0, 0.5) 50%,
      rgba(255, 215, 0, 0.3) 75%,
      transparent 100%
    );
    position: relative;
    z-index: 10;
    animation: shimmer 3s infinite;
  }

  .main-content {
    min-height: 0;
    flex: 1;
    display: flex;
    height: calc(100vh - calc(8 * var(--pickup-unit)));
    position: relative;
  }

  /* 左侧：准备中 */
  .preparing-section {
    min-width: 0;
    flex: 1;
    background: linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%);
    display: flex;
    flex-direction: column;
    padding: calc(50 * var(--pickup-unit)) calc(60 * var(--pickup-unit));
    position: relative;
    border-right: calc(2 * var(--pickup-unit)) solid rgba(255, 255, 255, 0.1);
    overflow: hidden;
  }

  .preparing-glow {
    background: radial-gradient(circle at 30% 20%, rgba(100, 150, 255, 0.15) 0%, transparent 50%);
    animation: glow 4s ease-in-out infinite;
  }

  .bg-glow {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
    z-index: 0;
  }

  .bg-pattern {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: radial-gradient(
        circle at 20% 30%,
        rgba(255, 255, 255, 0.02) calc(1 * var(--pickup-unit)),
        transparent calc(1 * var(--pickup-unit))
      ),
      radial-gradient(
        circle at 80% 70%,
        rgba(255, 255, 255, 0.02) calc(1 * var(--pickup-unit)),
        transparent calc(1 * var(--pickup-unit))
      );
    background-size: calc(50 * var(--pickup-unit)) calc(50 * var(--pickup-unit)),
      calc(80 * var(--pickup-unit)) calc(80 * var(--pickup-unit));
    pointer-events: none;
    z-index: 0;
  }

  /* 右侧：请取餐 */
  .ready-section {
    min-width: 0;
    flex: 1;
    background: linear-gradient(
        135deg,
        rgba(255, 215, 0, 0.4) 0%,
        rgba(255, 193, 7, 0.45) 25%,
        rgba(255, 165, 0, 0.5) 50%,
        rgba(255, 193, 7, 0.45) 75%,
        rgba(255, 215, 0, 0.4) 100%
      ),
      linear-gradient(45deg, #6b5238 0%, #7d6345 25%, #8b6f4f 50%, #7d6345 75%, #6b5238 100%);
    background-size: 100% 100%, 200% 200%;
    background-position: center, 0% 0%;
    background-blend-mode: overlay, normal;
    display: flex;
    flex-direction: column;
    padding: calc(50 * var(--pickup-unit)) calc(60 * var(--pickup-unit));
    position: relative;
    overflow: hidden;
  }

  .ready-glow {
    background: radial-gradient(
      circle at 70% 30%,
      rgba(255, 215, 0, 0.6) 0%,
      rgba(255, 193, 7, 0.4) 30%,
      rgba(255, 165, 0, 0.25) 50%,
      rgba(255, 140, 0, 0.15) 70%,
      transparent 85%
    );
    animation: glow 3s ease-in-out infinite;
  }

  .ready-pattern {
    background-image: radial-gradient(
        circle at 30% 20%,
        rgba(255, 215, 0, 0.1) calc(2 * var(--pickup-unit)),
        transparent calc(2 * var(--pickup-unit))
      ),
      radial-gradient(
        circle at 70% 80%,
        rgba(255, 193, 7, 0.1) calc(2 * var(--pickup-unit)),
        transparent calc(2 * var(--pickup-unit))
      );
    background-size: calc(60 * var(--pickup-unit)) calc(60 * var(--pickup-unit)),
      calc(90 * var(--pickup-unit)) calc(90 * var(--pickup-unit));
    opacity: 0.8;
  }

  .section-header {
    margin-bottom: calc(40 * var(--pickup-unit));
    position: relative;
    z-index: 2;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .title-wrapper {
    display: flex;
    align-items: center;
    gap: calc(20 * var(--pickup-unit));
  }

  .title-icon {
    width: calc(60 * var(--pickup-unit));
    height: calc(60 * var(--pickup-unit));
    background: linear-gradient(135deg, rgba(100, 150, 255, 0.2), rgba(150, 200, 255, 0.1));
    border-radius: calc(12 * var(--pickup-unit));
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: calc(28 * var(--pickup-unit));
    color: #64a5ff;
    box-shadow: 0 calc(4 * var(--pickup-unit)) calc(20 * var(--pickup-unit))
      rgba(100, 150, 255, 0.3);
    backdrop-filter: blur(calc(10 * var(--pickup-unit)));
    border: calc(1 * var(--pickup-unit)) solid rgba(100, 150, 255, 0.3);
  }

  .ready-icon {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 140, 0, 0.2));
    color: #ffd700;
    box-shadow: 0 calc(4 * var(--pickup-unit)) calc(20 * var(--pickup-unit)) rgba(255, 215, 0, 0.4);
    border: calc(1 * var(--pickup-unit)) solid rgba(255, 215, 0, 0.4);
  }

  .section-title {
    display: flex;
    flex-direction: column;
    gap: calc(5 * var(--pickup-unit));
    margin: 0;
  }

  .title-main {
    color: #fff;
    font-size: calc(44 * var(--pickup-unit));
    font-weight: 700;
    text-shadow: 0 0 calc(20 * var(--pickup-unit)) rgba(255, 255, 255, 0.3),
      calc(2 * var(--pickup-unit)) calc(2 * var(--pickup-unit)) calc(8 * var(--pickup-unit))
        rgba(0, 0, 0, 0.8);
    letter-spacing: calc(3 * var(--pickup-unit));
    line-height: 1.2;
  }

  .title-sub {
    color: rgba(255, 255, 255, 0.7);
    font-size: calc(20 * var(--pickup-unit));
    font-weight: 400;
    letter-spacing: calc(4 * var(--pickup-unit));
    text-transform: uppercase;
  }

  .section-count {
    width: calc(70 * var(--pickup-unit));
    height: calc(70 * var(--pickup-unit));
    background: linear-gradient(135deg, rgba(100, 150, 255, 0.2), rgba(150, 200, 255, 0.1));
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: calc(32 * var(--pickup-unit));
    font-weight: bold;
    color: #64a5ff;
    box-shadow: 0 0 calc(30 * var(--pickup-unit)) rgba(100, 150, 255, 0.4);
    backdrop-filter: blur(calc(10 * var(--pickup-unit)));
    border: calc(2 * var(--pickup-unit)) solid rgba(100, 150, 255, 0.3);
    animation: pulse 2s ease-in-out infinite;
  }

  .ready-count {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.3), rgba(255, 140, 0, 0.2));
    color: #ffd700;
    box-shadow: 0 0 calc(30 * var(--pickup-unit)) rgba(255, 215, 0, 0.5);
    border: calc(2 * var(--pickup-unit)) solid rgba(255, 215, 0, 0.4);
  }

  .order-numbers-container {
    min-height: 0;
    flex: 1;
    display: flex;
    flex-wrap: wrap;
    align-content: flex-start;
    gap: calc(15 * var(--pickup-unit));
    overflow-y: auto;
    padding-right: calc(15 * var(--pickup-unit));
    position: relative;
    z-index: 2;
  }

  .order-list-wrapper {
    --pickup-gap: calc(15 * var(--pickup-unit));

    display: grid;
    position: relative;
    /* 卡片至少 140px；空间足够时均分一行，最多 5 列。 */
    grid-template-columns: repeat(
      auto-fill,
      minmax(min(100%, max(140px, calc((100% - 4 * var(--pickup-gap)) / 5))), 1fr)
    );
    gap: var(--pickup-gap);
    width: 100%;
  }

  .order-number-item {
    container-type: inline-size;
    position: relative;
    aspect-ratio: 1.8;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(calc(20 * var(--pickup-unit)));
    border-radius: calc(12 * var(--pickup-unit));
    border: calc(1 * var(--pickup-unit)) solid rgba(255, 255, 255, 0.1);
    transition: border-color 0.25s ease, box-shadow 0.25s ease;
    backface-visibility: hidden;
    transform-origin: center;
    overflow: hidden;
    box-shadow: 0 calc(8 * var(--pickup-unit)) calc(32 * var(--pickup-unit)) rgba(0, 0, 0, 0.3);
    min-width: 0;
  }

  .order-number-item::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
    transition: left 0.5s;
  }

  .order-number-item:hover {
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 0 calc(12 * var(--pickup-unit)) calc(40 * var(--pickup-unit)) rgba(0, 0, 0, 0.4),
      0 0 calc(20 * var(--pickup-unit)) rgba(255, 255, 255, 0.1);
  }

  .order-number-item:hover::before {
    left: 100%;
  }

  .order-item-glow {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(100, 150, 255, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.4s;
    pointer-events: none;
  }

  .order-number-item:hover .order-item-glow {
    opacity: 1;
  }

  .ready-glow-item {
    background: radial-gradient(
      circle,
      rgba(255, 215, 0, 0.25) 0%,
      rgba(255, 193, 7, 0.15) 50%,
      transparent 70%
    );
  }

  /* 右侧订单项在更亮背景上的样式 */
  .ready-section .order-number-item {
    background: rgba(255, 255, 255, 0.18);
    border-color: rgba(255, 255, 255, 0.3);
    box-shadow: 0 calc(8 * var(--pickup-unit)) calc(32 * var(--pickup-unit)) rgba(0, 0, 0, 0.15),
      0 0 calc(20 * var(--pickup-unit)) rgba(255, 215, 0, 0.2);
  }

  .ready-section .order-number-item:hover {
    background: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.45);
    box-shadow: 0 calc(12 * var(--pickup-unit)) calc(40 * var(--pickup-unit)) rgba(0, 0, 0, 0.2),
      0 0 calc(30 * var(--pickup-unit)) rgba(255, 215, 0, 0.3);
  }

  .order-content {
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
  }

  .order-number {
    font-family: 'Courier New', 'Consolas', monospace;
    font-size: 25cqw;
    font-weight: 700;
    color: #fff;
    letter-spacing: 2cqw;
    text-shadow: 0 0 calc(20 * var(--pickup-unit)) rgba(255, 255, 255, 0.3),
      calc(2 * var(--pickup-unit)) calc(2 * var(--pickup-unit)) calc(8 * var(--pickup-unit))
        rgba(0, 0, 0, 0.8);
    white-space: nowrap;
  }

  .ready-section .order-number {
    color: #1f2937;
    text-shadow: none;
  }

  .order-number-item.vip-order {
    background: linear-gradient(135deg, rgba(255, 215, 0, 0.15), rgba(255, 140, 0, 0.1));
    border-color: rgba(255, 215, 0, 0.4);
    box-shadow: 0 calc(8 * var(--pickup-unit)) calc(32 * var(--pickup-unit)) rgba(255, 215, 0, 0.3);
  }

  .order-number-item.vip-order:hover {
    box-shadow: 0 calc(12 * var(--pickup-unit)) calc(40 * var(--pickup-unit)) rgba(255, 215, 0, 0.5),
      0 0 calc(30 * var(--pickup-unit)) rgba(255, 215, 0, 0.3);
  }

  .vip-icon {
    color: #ffd700;
    font-size: 18cqw;
    margin-right: 4cqw;
    filter: drop-shadow(0 0 calc(8 * var(--pickup-unit)) rgba(255, 215, 0, 0.8));
    animation: float 3s ease-in-out infinite;
    flex-shrink: 0;
  }

  .empty-state {
    width: 100%;
    text-align: center;
    margin-top: calc(150 * var(--pickup-unit));
    position: relative;
    z-index: 2;
  }

  .empty-icon {
    font-size: calc(80 * var(--pickup-unit));
    color: rgba(255, 255, 255, 0.2);
    margin-bottom: calc(20 * var(--pickup-unit));
    display: block;
    animation: float 3s ease-in-out infinite;
  }

  .empty-text {
    color: rgba(255, 255, 255, 0.5);
    font-size: calc(28 * var(--pickup-unit));
    margin: 0;
    letter-spacing: calc(2 * var(--pickup-unit));
  }

  /* 百叶窗翻转：新增翻开，移除合上，其余号码平滑补位。 */
  .order-list-enter-active,
  .order-list-leave-active {
    transition: transform 0.38s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.38s ease;
    pointer-events: none;
  }

  .order-list-leave-active {
    position: absolute;
    z-index: 3;
  }

  .order-list-enter-from {
    opacity: 0;
    transform: perspective(900px) rotateX(-90deg);
  }

  .order-list-leave-to {
    opacity: 0;
    transform: perspective(900px) rotateX(90deg);
  }

  .order-list-move {
    transition: transform 0.38s ease;
  }

  @media (prefers-reduced-motion: reduce) {
    .order-list-enter-active,
    .order-list-leave-active,
    .order-list-move {
      transition: none;
    }
  }

  /* 滚动条样式 */
  .order-numbers-container::-webkit-scrollbar {
    width: calc(10 * var(--pickup-unit));
  }

  .order-numbers-container::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: calc(10 * var(--pickup-unit));
  }

  .order-numbers-container::-webkit-scrollbar-thumb {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
    border-radius: calc(10 * var(--pickup-unit));
    border: calc(2 * var(--pickup-unit)) solid transparent;
    background-clip: padding-box;
  }

  .order-numbers-container::-webkit-scrollbar-thumb:hover {
    background: linear-gradient(180deg, rgba(255, 255, 255, 0.5), rgba(255, 255, 255, 0.3));
    background-clip: padding-box;
  }

  /* 小屏幕继续使用原有的紧凑布局。 */
  @media (max-width: 1366px) {
    .preparing-section,
    .ready-section {
      padding: 40px 50px;
    }

    .title-main {
      font-size: 38px;
    }

    .title-sub {
      font-size: 18px;
    }

    .section-count {
      width: 60px;
      height: 60px;
      font-size: 28px;
    }

    .title-icon {
      width: 50px;
      height: 50px;
      font-size: 24px;
    }

    .order-numbers-container {
      gap: 12px;
    }

    .order-list-wrapper {
      --pickup-gap: 12px;
    }
  }

  @media (max-width: 900px) {
    .main-content {
      flex-direction: column;
    }

    .preparing-section,
    .ready-section {
      min-height: 0;
      padding: 16px;
    }

    .section-header {
      margin-bottom: 20px;
    }

    .title-wrapper {
      gap: 12px;
    }

    .title-main {
      font-size: 28px;
    }

    .title-sub {
      font-size: 14px;
    }

    .title-icon,
    .section-count {
      width: 44px;
      height: 44px;
      font-size: 24px;
    }

    .order-numbers-container {
      padding-right: 0;
    }
  }
</style>
